import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Wand2, Loader2 } from "lucide-react";
import { enhancePrompt, generateMovie, getMovie } from "@/lib/api";
import GenerationProgress from "@/components/GenerationProgress";
import WaterBackground from "@/components/WaterBackground";

const GENRES = ["Sci-Fi", "Noir", "Fantasy", "Documentary", "Drama", "Horror", "Western", "Cinematic"];
const VOICES = [
  { id: "onyx", label: "Onyx — deep narrator" },
  { id: "fable", label: "Fable — storyteller" },
  { id: "nova", label: "Nova — energetic" },
  { id: "sage", label: "Sage — measured" },
  { id: "shimmer", label: "Shimmer — bright" },
  { id: "echo", label: "Echo — calm" },
];
const DURATIONS = [
  { v: 30,  label: "0:30",  hint: "5 scenes" },
  { v: 60,  label: "1:00",  hint: "10 scenes" },
  { v: 90,  label: "1:30",  hint: "15 scenes" },
  { v: 180, label: "3:00",  hint: "30 scenes" },
  { v: 300, label: "5:00",  hint: "50 scenes" },
];
const SIZES = [
  { v: "1280x720", label: "16:9  Widescreen" },
  { v: "1024x1792", label: "9:16  Vertical" },
  { v: "1024x1024", label: "1:1   Square" },
];

const SAMPLES = [
  "A river carves through a fog-drenched valley at dawn, a wooden canoe glides past mossy boulders.",
  "The Amazon at flood. A jaguar drinks at the black-mirror surface as macaws erupt overhead.",
  "A neon-lit river in a cyberpunk megacity, junkboats slip beneath a broken bridge in acid rain.",
  "A slow drift down a moonlit bayou, fireflies drifting between cypress knees, water like glass.",
];

export default function Studio() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [genre, setGenre] = useState("Cinematic");
  const [runtime, setRuntime] = useState(30);
  const [size, setSize] = useState("1280x720");
  const [voice, setVoice] = useState("onyx");
  const [withMusic, setWithMusic] = useState(true);
  const [enhancing, setEnhancing] = useState(false);
  const [job, setJob] = useState(null);
  const [movie, setMovie] = useState(null);
  const pollRef = useRef(null);

  const canSubmit = prompt.trim().length > 8 && !enhancing;
  const estMinutes = useMemo(
    () => Math.max(1, Math.round((runtime / 6) * 0.35)),
    [runtime]
  );

  useEffect(() => {
    if (!job?.id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const m = await getMovie(job.id);
        if (cancelled) return;
        setMovie(m);
        if (m.status === "complete") {
          toast.success("Movie is ready. Rolling to the screening room.");
          setTimeout(() => navigate(`/movie/${m.id}`), 900);
          return;
        }
        if (m.status === "failed") {
          toast.error(m.error || "Generation failed");
          return;
        }
        pollRef.current = setTimeout(tick, 3000);
      } catch (e) {
        pollRef.current = setTimeout(tick, 5000);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [job, navigate]);

  const onEnhance = async () => {
    if (!prompt.trim()) {
      toast.error("Type an idea first — even three words is enough.");
      return;
    }
    setEnhancing(true);
    try {
      const res = await enhancePrompt({ prompt, genre });
      if (res.prompt) {
        setPrompt(res.prompt);
        toast.success("Rewritten in director-grade prose.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rewrite failed");
    } finally {
      setEnhancing(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Give the story a little more shape — at least a sentence.");
      return;
    }
    try {
      const res = await generateMovie({
        prompt,
        genre,
        runtime,
        size,
        voice,
        music: withMusic,
      });
      setJob({ id: res.id });
      setMovie({ id: res.id, status: res.status });
      toast("Cameras rolling.", { description: `Estimated ~${estMinutes} min.` });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start generation");
    }
  };

  const inProgress = job && movie && movie.status !== "complete" && movie.status !== "failed";
  const isFailed = movie?.status === "failed";

  return (
    <div>
      {/* HERO — bright flowing water */}
      <section data-testid="studio-hero" className="relative min-h-[92vh] w-full overflow-hidden border-b border-white/10">
        <WaterBackground />

        <div className="relative mx-auto grid max-w-[1400px] gap-16 px-6 pb-24 pt-24 md:grid-cols-12 md:px-10 md:pt-32">
          <div className="md:col-span-12">
            <div data-testid="credits-card" className="mb-10 flex flex-col items-start gap-3 border-l-2 border-[#E50914] bg-white/50 px-5 py-3 backdrop-blur-md">
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.42em] text-[#0A2540]/70">
                Created by Kyle Hamilton
              </span>
              <span className="font-display italic text-sm tracking-[0.12em] text-[#0A2540]/85 md:text-base">
                A Free Determinism Production
              </span>
              <span className="font-display text-2xl font-black uppercase tracking-[0.14em] text-[#0A2540] md:text-3xl">
                REELFORGE <span className="text-[#E50914]">STUDIO</span>
              </span>
            </div>
            <div className="mb-8 flex items-center gap-4">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#E50914] rec-dot" />
              <span className="font-mono-ui text-[11px] uppercase tracking-[0.32em] text-[#0A2540]/70">
                Take 001 · Studio Session
              </span>
            </div>
            <h1 data-testid="hero-title" className="font-display text-[clamp(2.75rem,8vw,7rem)] font-black leading-[0.9] tracking-tight text-[#0A2540] drop-shadow-[0_2px_20px_rgba(255,255,255,0.55)]">
              Type a sentence.
              <br />
              Get a <span className="italic text-[#E50914]">short film</span>.
            </h1>
            <p className="mt-8 max-w-2xl font-mono-ui text-sm uppercase tracking-[0.24em] text-[#0A2540]/70">
              100% Free · Flux Image + Ken Burns · Edge Neural Voice · ffmpeg Score
            </p>
          </div>

          {!inProgress && !isFailed && (
            <form onSubmit={onSubmit} className="md:col-span-12" data-testid="studio-form">
              <div className="relative border border-white/60 bg-white/85 shadow-[0_20px_80px_-20px_rgba(10,37,64,0.35)] backdrop-blur-xl">
                <div className="flex items-center justify-between border-b border-[#0A2540]/10 px-6 py-3">
                  <span className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[#0A2540]/60">
                    FADE IN:
                  </span>
                  <span className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[#0A2540]/50">
                    {prompt.length} chars
                  </span>
                </div>
                <textarea
                  data-testid="prompt-input"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="EXT. RIVER - DAWN. A wooden canoe glides through fog as light slices through the pines..."
                  rows={5}
                  className="w-full resize-none bg-transparent px-6 py-6 font-mono-ui text-xl leading-relaxed text-[#0A2540] outline-none placeholder:text-[#0A2540]/30 md:text-2xl"
                />
                <div className="flex flex-wrap items-center gap-2 border-t border-[#0A2540]/10 bg-white/40 px-6 py-3">
                  <button
                    type="button"
                    data-testid="enhance-button"
                    onClick={onEnhance}
                    disabled={enhancing || !prompt.trim()}
                    className="group inline-flex items-center gap-2 border border-[#E50914] bg-[#E50914] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white transition-colors duration-200 hover:bg-[#0A2540] hover:border-[#0A2540] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {enhancing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
                    ) : (
                      <Wand2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    )}
                    Make it more cinematic
                  </button>
                  <span className="mx-2 font-mono-ui text-[10px] uppercase tracking-[0.28em] text-[#0A2540]/40">
                    or try
                  </span>
                  {SAMPLES.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`sample-${i}`}
                      onClick={() => setPrompt(s)}
                      className="border border-[#0A2540]/15 bg-white/70 px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.24em] text-[#0A2540]/70 transition-colors duration-200 hover:border-[#E50914] hover:text-[#0A2540]"
                    >
                      {s.split(",")[0].slice(0, 34)}…
                    </button>
                  ))}
                </div>
              </div>

              {/* CONTROLS ROW */}
              <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-4">
                <ControlGroup label="Genre" testid="control-genre">
                  <div className="flex flex-wrap gap-1.5">
                    {GENRES.map((g) => (
                      <button
                        type="button"
                        key={g}
                        data-testid={`genre-${g}`}
                        onClick={() => setGenre(g)}
                        className={`border px-2.5 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.22em] transition-colors duration-200 ${
                          genre === g
                            ? "border-[#E50914] bg-[#E50914] text-white"
                            : "border-[#0A2540]/20 bg-white text-[#0A2540]/70 hover:border-[#0A2540] hover:text-[#0A2540]"
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </ControlGroup>

                <ControlGroup label="Runtime" testid="control-runtime">
                  <div className="flex flex-col gap-1.5">
                    {DURATIONS.map((d) => (
                      <button
                        key={d.v}
                        type="button"
                        data-testid={`runtime-${d.v}`}
                        onClick={() => setRuntime(d.v)}
                        className={`flex items-center justify-between border px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.22em] transition-colors duration-200 ${
                          runtime === d.v
                            ? "border-[#E50914] bg-[#E50914] text-white"
                            : "border-[#0A2540]/20 bg-white text-[#0A2540]/70 hover:border-[#0A2540] hover:text-[#0A2540]"
                        }`}
                      >
                        <span>{d.label}</span>
                        <span className="opacity-70">{d.hint}</span>
                      </button>
                    ))}
                  </div>
                </ControlGroup>

                <ControlGroup label="Aspect" testid="control-aspect">
                  <div className="flex flex-col gap-1.5">
                    {SIZES.map((s) => (
                      <button
                        type="button"
                        key={s.v}
                        data-testid={`size-${s.v}`}
                        onClick={() => setSize(s.v)}
                        className={`border px-3 py-2 text-left font-mono-ui text-[10px] uppercase tracking-[0.22em] transition-colors duration-200 ${
                          size === s.v
                            ? "border-[#E50914] bg-[#E50914] text-white"
                            : "border-[#0A2540]/20 bg-white text-[#0A2540]/70 hover:border-[#0A2540] hover:text-[#0A2540]"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </ControlGroup>

                <ControlGroup label="Voice · Score" testid="control-voice">
                  <select
                    data-testid="voice-select"
                    value={voice}
                    onChange={(e) => setVoice(e.target.value)}
                    className="w-full border border-[#0A2540]/20 bg-white px-3 py-2 font-mono-ui text-xs tracking-widest text-[#0A2540] outline-none focus:border-[#E50914]"
                  >
                    {VOICES.map((v) => (
                      <option key={v.id} value={v.id} className="bg-white">
                        {v.label}
                      </option>
                    ))}
                  </select>
                  <label className="mt-2 flex items-center gap-2 border border-[#0A2540]/20 bg-white px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.22em] text-[#0A2540]/80">
                    <input
                      type="checkbox"
                      data-testid="music-toggle"
                      checked={withMusic}
                      onChange={(e) => setWithMusic(e.target.checked)}
                      className="accent-[#E50914]"
                    />
                    Auto-score with music bed
                  </label>
                </ControlGroup>
              </div>

              <div className="mt-8 flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
                <p className="max-w-md font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[#0A2540]/60">
                  Est. render · ~{estMinutes} min · you&rsquo;ll be walked into the screening room when it&rsquo;s ready.
                </p>
                <button
                  data-testid="generate-button"
                  type="submit"
                  disabled={!canSubmit}
                  className="group flex items-center gap-4 border border-[#E50914] bg-[#E50914] px-8 py-4 font-mono-ui text-xs uppercase tracking-[0.32em] text-white shadow-[0_10px_30px_-10px_rgba(229,9,20,0.55)] transition-all duration-200 hover:bg-[#0A2540] hover:border-[#0A2540] disabled:cursor-not-allowed disabled:border-white/40 disabled:bg-white/40 disabled:text-white/60 disabled:shadow-none"
                >
                  <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                  Roll Camera
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.5} />
                </button>
              </div>
            </form>
          )}

          {(inProgress || isFailed) && movie && (
            <div className="md:col-span-12">
              <GenerationProgress movie={movie} />
              {isFailed && (
                <button
                  data-testid="reset-button"
                  onClick={() => {
                    setJob(null);
                    setMovie(null);
                  }}
                  className="mt-6 border border-white/15 px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.28em] text-white/80 hover:border-[#E50914] hover:text-white"
                >
                  Try Another Take
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS STRIP */}
      <section className="border-b border-white/10 bg-[#050505] py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mb-16 flex items-end justify-between">
            <div>
              <div className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-white/40">
                / 04 stages / one prompt
              </div>
              <h2 className="mt-3 font-display text-5xl font-bold leading-none tracking-tight md:text-6xl">
                The pipeline.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-px bg-white/10 md:grid-cols-4">
            {[
              { n: "01", t: "Screenplay", d: "A free text model drafts your title, logline, per-scene shot notes, and a voice-over paragraph tuned to your runtime." },
              { n: "02", t: "Cinematography", d: "Free Flux image gen paints every scene, then ffmpeg pans, zooms and pushes for a real Ken Burns cinematic feel." },
              { n: "03", t: "Voice", d: "Microsoft Edge neural voices read the narration — completely free and high-quality." },
              { n: "04", t: "Score", d: "A mood-matched instrumental bed is synthesized locally with ffmpeg and mixed under the voice — fades in and out like a real film." },
            ].map((c) => (
              <div key={c.n} className="bg-[#050505] p-10">
                <div className="font-mono-ui text-[11px] uppercase tracking-[0.32em] text-[#E50914]">
                  {c.n}
                </div>
                <div className="mt-6 font-display text-2xl font-bold tracking-tight">{c.t}</div>
                <p className="mt-4 text-sm leading-relaxed text-white/60">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function ControlGroup({ label, children, testid }) {
  return (
    <div data-testid={testid} className="border border-white/50 bg-white/75 p-4 backdrop-blur-md">
      <div className="mb-3 font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[#0A2540]/50">
        {label}
      </div>
      {children}
    </div>
  );
}
